import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, FormControlLabel, Switch } from "@material-ui/core";
import {
  normalizationSettings,
  STANDARD_NORMALIZATION
} from "./descriptionNormalization";
import styles from "./DescriptionParametersSection.module.scss";

export default function DescriptionParameterNormalization({
  decision,
  examples,
  disabled,
  onChange,
  api,
  tr
}) {
  const options = normalizationSettings(decision);
  const active = options.letter_case !== "original" || options.trailing_period;
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const request = JSON.stringify({ values: examples.slice(0, 5), decision });
  useEffect(() => {
    let live = true;
    setResult(null);
    setError(false);
    setLoading(active);
    if (!active) return undefined;
    const timer = setTimeout(async () => {
      try {
        const data = await api("preview", JSON.parse(request));
        if (live) setResult(data);
      } catch (_failure) {
        if (live) setError(true);
      } finally {
        if (live) setLoading(false);
      }
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [active, api, request, retry]);

  return (
    <section className={styles.normalizationControls}>
      <FormControlLabel
        className={styles.normalizationToggle}
        classes={{ label: styles.normalizationToggleLabel }}
        data-enabled={active}
        label={tr("normalizeValues")}
        labelPlacement="start"
        control={
          <Switch
            color="primary"
            checked={active}
            disabled={disabled}
            onChange={event =>
              onChange({
                value_normalization: event.target.checked
                  ? { ...STANDARD_NORMALIZATION }
                  : { letter_case: "original", trailing_period: false }
              })
            }
          />
        }
      />
      {active && loading && (
        <span role="status" className={styles.controlHelp}>
          {tr("previewLoading")}
        </span>
      )}
      {active && error && (
        <div role="alert" className={styles.previewFailure}>
          <span>{tr("previewUnavailable")}</span>
          <Button size="small" onClick={() => setRetry(value => value + 1)}>
            {tr("retryPreview")}
          </Button>
        </div>
      )}
      {active && result && (
        <div
          className={styles.normalizationPreview}
          data-testid="normalization-preview"
        >
          <span className={styles.controlHelp}>
            {tr("normalizationSampleCount", {
              before: result.source_count,
              after: result.value_count
            })}
          </span>
          {result.groups.length ? (
            <table>
              <thead>
                <tr>
                  <th>{tr("beforeNormalization")}</th>
                  <th>{tr("afterNormalization")}</th>
                </tr>
              </thead>
              <tbody>
                {result.groups.map(group => (
                  <tr key={group.value}>
                    <td>
                      {group.sources.map(value => (
                        <span key={value}>{value}</span>
                      ))}
                    </td>
                    <td>{group.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>{tr("noNormalizedValues")}</p>
          )}
          {result.truncated && (
            <span className={styles.controlHelp}>{tr("previewTruncated")}</span>
          )}
        </div>
      )}
    </section>
  );
}

DescriptionParameterNormalization.propTypes = {
  decision: PropTypes.object.isRequired,
  examples: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  api: PropTypes.func.isRequired,
  tr: PropTypes.func.isRequired
};
